import { Link } from 'react-router-dom';
import { filamentCompareData } from '../../data/filamentCompare';
import { useFilamentText } from '../../hooks/useFilamentText';

interface BendingStrengthCardProps {
  slug: string;
  isEnglish?: boolean;
}

/**
 * 曲げ強度カード（動的取得版）
 * 全フィラメント個別ページで共通使用。パターン1(デフォルト)とパターン2(Z方向)を自動判定。
 */
const BendingStrengthCard = ({ slug, isEnglish = false }: BendingStrengthCardProps) => {
  const TEXT = useFilamentText();
  const data = filamentCompareData.find(d => d.slug === slug);

  if (!data) return null;

  return (
    <div className="metal-panel p-6">
      <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
        {TEXT.bendingStrength}
      </h2>
      
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-metal">
              <th className="py-3 px-4 text-metal-light font-semibold">{TEXT.item}</th>
              <th className="py-3 px-4 text-metal-light font-semibold">{isEnglish ? 'Measured Value' : '測定値'}</th>
            </tr>
          </thead>
          <tbody>
            {/* パターン1: デフォルト */}
            <tr className="border-b border-metal/50 bg-[#2a2d32]">
              <td className="py-2 px-4 text-white font-bold" colSpan={2}>
                {isEnglish ? 'Default (Flat Layout)' : 'デフォルト (平置き)'}
              </td>
            </tr>
            <tr className="border-b border-metal/50">
              <td className="py-3 px-4 text-white pl-8">{TEXT.bendingStrengthValue}</td>
              <td className="py-3 px-4 text-metal-light">{data.bendStrengthMPa ? `${data.bendStrengthMPa} MPa` : 'N/A'}</td>
            </tr>
            <tr className="border-b border-metal/50">
              <td className="py-3 px-4 text-white pl-8">{TEXT.flexuralModulus || (isEnglish ? 'Flexural Modulus' : '曲げ弾性率')}</td>
              <td className="py-3 px-4 text-metal-light">{data.flexuralModulusMPa ? `${data.flexuralModulusMPa} MPa` : 'N/A'}</td>
            </tr>

            {/* パターン2: Z方向の強度（データがある場合のみ表示） */}
            {data.zAxisStrengthMPa && (
              <>
                <tr className="border-b border-metal/50 bg-[#2a2d32]">
                  <td className="py-2 px-4 text-white font-bold mt-2" colSpan={2}>
                    {isEnglish ? 'Z-Axis Strength (Vertical Layout)' : 'Z方向の強度 (縦置き)'}
                  </td>
                </tr>
                <tr className="border-b border-metal/50">
                  <td className="py-3 px-4 text-white pl-8">{TEXT.bendingStrengthValue}</td>
                  <td className="py-3 px-4 text-metal-light">{data.zAxisStrengthMPa} MPa</td>
                </tr>
                <tr className="border-b border-metal/50">
                  <td className="py-3 px-4 text-white pl-8">{TEXT.flexuralModulus || (isEnglish ? 'Flexural Modulus' : '曲げ弾性率')}</td>
                  <td className="py-3 px-4 text-metal-light">{data.zAxisModulusMPa ? `${data.zAxisModulusMPa} MPa` : 'N/A'}</td>
                </tr>
              </>
            )}

            <tr>
              <td className="py-3 px-4 text-white">{TEXT.testMethod}</td>
              <td className="py-3 px-4">
                {TEXT.threePointBending}（
                <Link 
                  to={isEnglish ? "/en/filament/bend-strength-test" : "/filament/bend-strength-test"}
                  className="text-accent hover:text-accent/80 transition-colors"
                >
                  {TEXT.details}
                </Link>
                ）
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BendingStrengthCard;
